import { clsx } from 'clsx';
import type { HTMLAttributes, ReactNode } from 'react';

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('card', className)} {...props} />;
}

export function CardHeader({ title, subtitle, action, icon }: {
  title: ReactNode; subtitle?: ReactNode; action?: ReactNode; icon?: ReactNode;
}) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-line px-4 py-3">
      <div className="flex items-start gap-3">
        {icon && <span className="mt-0.5 text-brand-600 dark:text-brand-400">{icon}</span>}
        <div>
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {subtitle && <p className="mt-0.5 text-xs text-ink-muted">{subtitle}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

export function CardBody({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={clsx('p-4', className)} {...props} />;
}

/** Dashboard KPI tile: label, big value, delta and an optional icon. */
export function StatCard({ label, value, delta, deltaKind, icon, hint }: {
  label: string; value: string; delta?: string; deltaKind?: 'up' | 'down' | 'flat';
  icon?: ReactNode; hint?: string;
}) {
  const deltaColor = deltaKind === 'up' ? 'text-emerald-600 dark:text-emerald-400'
    : deltaKind === 'down' ? 'text-red-600 dark:text-red-400' : 'text-ink-muted';
  return (
    <Card className="p-4">
      <div className="flex items-start justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-ink-muted">{label}</p>
        {icon && <span className="text-ink-faint">{icon}</span>}
      </div>
      <p className="mt-2 text-2xl font-semibold tabular-nums text-ink">{value}</p>
      <div className="mt-1 flex items-center gap-2">
        {delta && <span className={clsx('text-xs font-medium', deltaColor)}>{delta}</span>}
        {hint && <span className="text-xs text-ink-faint">{hint}</span>}
      </div>
    </Card>
  );
}
